import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { companyInfo } from "../../data/mock";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const navigation = [
    { name: t("nav.services"), href: "/services" },
    { name: t("nav.caseStudies"), href: "/case-studies" },
    { name: t("nav.about"), href: "/about" },
    { name: t("nav.blog"), href: "/blog" },
    { name: t("nav.contact"), href: "/contact" },
  ];

  return (
    <footer
      className="bg-black border-t border-white/10"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center mb-6">
              <img
                src="/logo-icon-only.png"
                alt="Solid Consulting"
                className="h-10 w-auto"
                width="34"
                height="40"
                loading="lazy"
              />
            </Link>
            <p className="text-white/60 text-base max-w-md mb-6">
              {t("footer.description")}
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-3 text-white/60 hover:text-[#00FFD1] transition-colors duration-300"
              >
                <Mail className="w-4 h-4" />
                <span>{companyInfo.email}</span>
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-3 text-white/60 hover:text-[#00FFD1] transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                <span>{companyInfo.phone}</span>
              </a>
              <div className="flex items-center gap-3 text-white/60">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>{companyInfo.address}</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold text-base mb-6">
              {t("footer.navigation")}
            </h4>
            <nav className="flex flex-col gap-3">
              <Link
                to="/"
                className="text-white/60 hover:text-[#00FFD1] transition-colors duration-300 flex items-center gap-2"
              >
                {t("footer.home")}
              </Link>
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-white/60 hover:text-[#00FFD1] transition-colors duration-300 flex items-center gap-2"
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* CTA */}
          <div>
            <h4 className="text-white font-semibold text-base mb-6">
              {t("footer.getStarted")}
            </h4>
            <p className="text-white/60 text-sm mb-6">
              {t("footer.getStartedDesc")}
            </p>
            <Link
              to="/contact"
              className={`inline-flex items-center gap-2 bg-[#00FFD1] text-black px-6 py-3 font-medium hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-400 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              {t("nav.bookCall")}
              <ArrowUpRight className={`w-4 h-4 ${isRTL ? "rotate-90" : ""}`} />
            </Link>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © {currentYear} Solid Consulting. {t("footer.rights")}
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="text-white/60 hover:text-white text-sm transition-colors duration-300"
            >
              {t("footer.privacy")}
            </a>
            <a
              href="#"
              className="text-white/60 hover:text-white text-sm transition-colors duration-300"
            >
              {t("footer.terms")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
